import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Truck, Plus, Pencil, Trash2, Repeat } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { PhotoCapture } from "../components/PhotoCapture";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EMPTY = { matricula: "", chasis_numero: "", es_traspaso: false };

export default function Camiones() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [foto, setFoto] = useState(null);
  const [chasisFoto, setChasisFoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: trucks = [] } = useQuery({ queryKey: ["trucks"], queryFn: async () => (await api.get("/trucks")).data });

  const openNew = () => { setEditing(null); setForm(EMPTY); setFoto(null); setChasisFoto(null); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ matricula: t.matricula, chasis_numero: t.chasis_numero, es_traspaso: !!t.es_traspaso }); setFoto(t.foto || null); setChasisFoto(t.chasis_foto || null); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, foto, chasis_foto: chasisFoto };
    try {
      if (editing) await api.put(`/trucks/${editing.id}`, payload);
      else await api.post("/trucks", payload);
      toast.success(editing ? "Camión actualizado" : "Camión registrado");
      qc.invalidateQueries({ queryKey: ["trucks"] });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar el camión ${t.matricula}?`)) return;
    await api.delete(`/trucks/${t.id}`);
    toast.success("Camión eliminado");
    qc.invalidateQueries({ queryKey: ["trucks"] });
  };

  return (
    <div className="space-y-5" data-testid="camiones-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl md:text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Truck className="w-6 h-6 text-cyan-600" /> Camiones
          </h1>
          <p className="text-sm text-slate-500">Flota registrada con soporte para camiones de traspaso</p>
        </div>
        <Button onClick={openNew} className="bg-navy hover:bg-navy-800 text-white" data-testid="new-truck-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Camión
        </Button>
      </div>

      {trucks.length === 0 && (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400 text-sm" data-testid="trucks-empty">No hay camiones registrados</CardContent></Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {trucks.map((t) => (
          <Card key={t.id} className="overflow-hidden border-slate-200 hover:shadow-md transition-shadow" data-testid={`truck-card-${t.matricula}`}>
            <div className="h-36 bg-navy relative">
              {t.foto ? <img src={t.foto} alt={t.matricula} className="w-full h-full object-cover" /> :
                <div className="w-full h-full flex items-center justify-center"><Truck className="w-10 h-10 text-slate-600" /></div>}
              {t.es_traspaso && (
                <Badge className="absolute top-2 right-2 bg-purple-600 text-white border-0" data-testid={`truck-traspaso-badge-${t.matricula}`}>
                  <Repeat className="w-3 h-3 mr-1" /> Traspaso
                </Badge>
              )}
            </div>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold text-lg tracking-wider text-navy">{t.matricula}</span>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(t)} className="p-2 text-slate-400 hover:text-cyan-600 transition-colors" data-testid={`edit-truck-${t.matricula}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(t)} className="p-2 text-slate-400 hover:text-red-600 transition-colors" data-testid={`delete-truck-${t.matricula}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-xs text-slate-500">Chasis: <span className="font-mono">{t.chasis_numero}</span></p>
              {t.chasis_foto && <img src={t.chasis_foto} alt="Chasis" className="w-full h-20 object-cover rounded-md border border-slate-100" />}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="truck-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">{editing ? "Editar Camión" : "Registrar Camión"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="matricula">Matrícula</Label>
                <Input id="matricula" required value={form.matricula} onChange={(e) => setForm({ ...form, matricula: e.target.value.toUpperCase() })}
                       placeholder="ABC-123" className="font-mono uppercase" data-testid="truck-matricula-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="chasis">Número de chasis</Label>
                <Input id="chasis" required value={form.chasis_numero} onChange={(e) => setForm({ ...form, chasis_numero: e.target.value })}
                       placeholder="CH-000000" className="font-mono" data-testid="truck-chasis-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.es_traspaso} onCheckedChange={(v) => setForm({ ...form, es_traspaso: !!v })} data-testid="truck-traspaso-checkbox" />
              <span className="text-sm text-slate-600">Es camión de traspaso</span>
            </label>
            <PhotoCapture label="Foto del camión" value={foto} onChange={setFoto} testid="truck-photo" />
            <PhotoCapture label="Foto del chasis" value={chasisFoto} onChange={setChasisFoto} testid="truck-chasis-photo" />
            <Button type="submit" disabled={saving} className="w-full bg-navy hover:bg-navy-800 text-white h-11" data-testid="truck-save-button">
              {saving ? "Guardando..." : editing ? "Guardar Cambios" : "Registrar Camión"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
